import {
  type ConsultationRequestDTO,
  ConsultationRequestStatusDTO,
  type ConsultationRequestWithCaseDTO,
  type ScheduledConsultationCaseReportDTO,
  type ScheduledConsultationDTO,
  ScheduledConsultationStatusDTO,
  type UpdateScheduledConsultationParticipantDTO,
  UrgencyDTO
} from '@/generated'
import { ConsultationService } from '@/services/telehealthApi/ConsultationService'
import { NoticeType } from '@/stores/models/Notice'
import { useNoticeStore } from '@/stores/notice'
import { useLocalStorage } from '@vueuse/core'
import { remove } from 'lodash'
import { defineStore } from 'pinia'
import { computed, type Ref, ref } from 'vue'
import type { ConsultationRequestFilter } from './models/ConsultationRequestFilter'
import { useUserStore } from './user'
import { useI18n } from 'vue-i18n'
import { useAccessibleUsersStore } from '@/stores/accessibleUsers'
import { isOwnConsultation } from '@/util/supervisedConsultations'

export const useConsultationRequestStore = defineStore('consultationRequest', () => {
  const { t } = useI18n()

  const incomingDirectConsultations: Ref<ConsultationRequestDTO[]> = ref([])
  const isInitialLoadingIncomingDirectConsultations = ref(true)

  const outgoingDirectConsultations: Ref<ConsultationRequestDTO[]> = ref([])
  const isInitialLoadingOutgoingDirectConsultations = ref(true)

  const incomingScheduledConsultations: Ref<ScheduledConsultationDTO[]> = ref([])
  const outgoingScheduledConsultations: Ref<ScheduledConsultationDTO[]> = ref([])

  const currentScheduledConsultation: Ref<ScheduledConsultationDTO | null> = ref(null)
  const isLoadingCurrentConsultation = ref(false)

  const currentScheduledConsultationReports: Ref<ScheduledConsultationCaseReportDTO[]> = ref([])
  const isLoadingCurrentConsultationReports = ref(false)

  const isEditingRecipients = ref(false)
  const userStore = useUserStore()
  const accessibleUsersStore = useAccessibleUsersStore()

  const consultationRequestFilter: Ref<ConsultationRequestFilter> = useLocalStorage(
    'consultationRequestFilter',
    {
      hospitals: [],
      targetMedicalFields: [],
      departments: [],
      wards: [],
      urgencies: [],
      assignees: [],
      requesters: []
    }
  )
  const consultationRequestFilterInput: Ref<ConsultationRequestFilter> = ref({
    hospitals: [],
    targetMedicalFields: [],
    departments: [],
    wards: [],
    urgencies: [],
    assignees: [],
    requesters: []
  })

  const areReportsPending = computed<boolean>(
    () =>
      hasDirectConsultationReportPending(incomingDirectConsultations.value) ||
      hasScheduledConsultationReportPending(incomingScheduledConsultations.value)
  )

  function hasDirectConsultationReportPending(
    directConsultations: ConsultationRequestDTO[]
  ): boolean {
    return (
      directConsultations.filter(
        (consultation) =>
          isOwnConsultation(consultation) &&
          consultation.status === ConsultationRequestStatusDTO.REPORT_PENDING
      ).length > 0
    )
  }

  function hasScheduledConsultationReportPending(
    scheduledConsultations: ScheduledConsultationDTO[]
  ): boolean {
    return (
      scheduledConsultations.filter(({ status, participants }) => {
        const participant = participants.find(({ user }) =>
          userStore.isTheSameAsUser(user.userName)
        )
        return (
          status === ScheduledConsultationStatusDTO.REPORT_PENDING &&
          participant?.shouldWriteReport === true &&
          participant?.reportFinalized !== true
        )
      }).length > 0
    )
  }

  const getDirectConsultationsWithActiveCall = computed<ConsultationRequestDTO[]>(() =>
    [...incomingDirectConsultations.value, ...outgoingDirectConsultations.value].filter(
      (cr) => cr.hasActiveCall === true
    )
  )

  const getScheduledConsultationsWithActiveCall = computed<ScheduledConsultationDTO[]>(() =>
    [...incomingScheduledConsultations.value, ...outgoingScheduledConsultations.value].filter(
      (cr) => cr.hasActiveCall === true
    )
  )

  const noticeStore = useNoticeStore()

  async function refreshIncomingConsultationRequestList(): Promise<void> {
    if (!userStore.canViewIncomingConsultations) {
      console.log('User cannot receive consultations, skipping refresh')
      return
    }

    try {
      const incomingConsultationsResponse =
        await ConsultationService.getIncomingConsultationRequests()

      incomingDirectConsultations.value = incomingConsultationsResponse.consultationRequests
      incomingScheduledConsultations.value =
        incomingConsultationsResponse.scheduledConsultationRequests
    } catch (err) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: 'Loading the consultation requests failed'
      })
    } finally {
      isInitialLoadingIncomingDirectConsultations.value = false
    }
  }

  // TODO(https://tcc-cs.atlassian.net/browse/TSDC-1821 Handle pagination for outgoing consultation requests")
  async function refreshOutgoingConsultationRequestList(): Promise<void> {
    if (!userStore.canViewOutgoingConsultations) {
      return
    }

    try {
      const outgoingConsultationsResponse =
        await ConsultationService.getOutgoingConsultationRequests()

      outgoingDirectConsultations.value = mergeById(
        outgoingDirectConsultations.value,
        outgoingConsultationsResponse.consultationRequests
      )

      outgoingScheduledConsultations.value = mergeById(
        outgoingScheduledConsultations.value,
        outgoingConsultationsResponse.scheduledConsultationRequests
      )
    } catch (err) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: 'Loading the consultation requests failed'
      })
    } finally {
      isInitialLoadingOutgoingDirectConsultations.value = false
    }
  }

  async function deleteOutgoingDirectConsultationById(id: string): Promise<void> {
    try {
      await ConsultationService.deleteConsultationRequest(id)
    } catch (error) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: 'Deleting the consultation request {id} failed',
        messageParameters: { id }
      })
    }
    const indexToDelete = outgoingDirectConsultations.value.findIndex(
      (request: ConsultationRequestDTO) => request.id === id
    )
    outgoingDirectConsultations.value.splice(indexToDelete, 1)
  }

  async function deleteOutgoingScheduledConsultationById(id: string): Promise<void> {
    try {
      await ConsultationService.deleteScheduledConsultationRequest(id)
      const indexToDelete = outgoingScheduledConsultations.value.findIndex(
        (request: ScheduledConsultationDTO) => request.id === id
      )
      outgoingScheduledConsultations.value.splice(indexToDelete, 1)
      await refreshIncomingConsultationRequestList()
    } catch (error) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: t('Deleting the scheduled consultation request {id} failed'),
        messageParameters: { id }
      })
    }
  }

  async function completeScheduledConsultationById(id: string): Promise<void> {
    try {
      await ConsultationService.completeScheduledConsultationRequest(id)
      await Promise.all([
        refreshOutgoingConsultationRequestList(),
        refreshScheduledConsultationById(id, { withClear: false })
      ])
    } catch (error) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: 'Completing the scheduled consultation request {id} failed',
        messageParameters: { id }
      })
    }
  }

  async function updateScheduledConsultationCases(
    consultationId: string,
    cases: string[]
  ): Promise<void> {
    try {
      await ConsultationService.updateScheduledConsultationRequestCases(consultationId, cases)
      await Promise.all([
        refreshOutgoingConsultationRequestList(),
        refreshScheduledConsultationById(consultationId, { withClear: false })
      ])
    } catch (error) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: 'Updating the cases of scheduled consultation {id} failed',
        messageParameters: { id: consultationId }
      })
    }
  }

  async function hideScheduledConsultationById(consultationId: string): Promise<void> {
    try {
      await ConsultationService.hideScheduledConsultationRequest(consultationId)
      await Promise.all([
        refreshIncomingConsultationRequestList(),
        refreshOutgoingConsultationRequestList(),
        refreshScheduledConsultationById(consultationId, { withClear: false })
      ])
    } catch (error) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: 'Hidding the scheduled consultation {id} failed',
        messageParameters: { id: consultationId }
      })
    }
  }

  async function updateScheduledConsultationRecipients(
    consultationId: string,
    participants: UpdateScheduledConsultationParticipantDTO[],
    requesterShouldWriteReport: boolean = false
  ): Promise<void> {
    try {
      await ConsultationService.updateScheduledConsultationRequestRecipients(
        consultationId,
        participants,
        requesterShouldWriteReport
      )
      await Promise.all([
        refreshIncomingConsultationRequestList(),
        refreshScheduledConsultationById(consultationId, { withClear: false })
      ])
    } catch (error) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: 'Updating the recipients of scheduled consultation {id} failed',
        messageParameters: { id: consultationId }
      })
    }
  }

  function getIncomingDirectConsultationById(id: string): ConsultationRequestDTO | undefined {
    return incomingDirectConsultations.value.find((request) => request.id === id)
  }

  function getIncomingScheduledConsultationById(id: string): ScheduledConsultationDTO | null {
    return incomingScheduledConsultations.value.find((request) => request.id === id) ?? null
  }

  function getOutgoingDirectConsultationById(id: string): ConsultationRequestDTO | undefined {
    return outgoingDirectConsultations.value.find((request) => request.id === id)
  }

  function getOutgoingScheduledConsultationById(id: string): ScheduledConsultationDTO | null {
    return outgoingScheduledConsultations.value.find((request) => request.id === id) ?? null
  }

  const areUsersLoading = computed<boolean>(() => accessibleUsersStore.isLoadingUsers)

  async function refreshUsers(): Promise<void> {
    if (accessibleUsersStore.users.length === 0) {
      await accessibleUsersStore.refreshUsers()
    }
  }

  async function getDirectConsultationById(
    id: string
  ): Promise<ConsultationRequestWithCaseDTO | undefined> {
    return (
      await ConsultationService.findConsultationRequestsByFilter({ id: id })
    ).consultationRequestsWithCase.find((request) => request.id === id)
  }

  async function refreshScheduledConsultationById(
    id: string,
    { withClear = true }: { withClear?: boolean } = {}
  ): Promise<void> {
    isLoadingCurrentConsultation.value = true

    if (withClear) {
      currentScheduledConsultation.value = null
      currentScheduledConsultationReports.value = []
      isEditingRecipients.value = false
    }

    try {
      currentScheduledConsultation.value =
        await ConsultationService.getScheduledConsultationRequest(id)
    } catch (error) {
      const localConsultation = [
        ...incomingScheduledConsultations.value,
        ...outgoingScheduledConsultations.value
      ].find((consultation) => consultation.id === id)

      if (!localConsultation) {
        noticeStore.add({
          type: NoticeType.ERROR,
          message: 'Consultation request not found'
        })
        return
      }

      currentScheduledConsultation.value = localConsultation
    } finally {
      isLoadingCurrentConsultation.value = false
    }
  }

  async function refreshScheduledConsultationReportsById(id: string): Promise<void> {
    isLoadingCurrentConsultationReports.value = true

    try {
      const reports = await ConsultationService.getScheduledConsultationReports(id)
      currentScheduledConsultationReports.value = reports.caseReports
    } catch (error) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: 'Reports not found'
      })
      return
    } finally {
      isLoadingCurrentConsultationReports.value = false
    }
  }

  function mergeById<T extends { id: unknown }>(target: T[], source: T[]): T[] {
    return source.map((sourceElem) => {
      const targetElem = target.find(
        (potentialTargetElem) => potentialTargetElem.id === sourceElem.id
      )
      return targetElem ? Object.assign(targetElem, sourceElem) : sourceElem
    })
  }

  function commitConsultationRequestFilterInput() {
    consultationRequestFilter.value = { ...consultationRequestFilterInput.value }
  }

  function resetConsultationRequestFilterInput() {
    consultationRequestFilterInput.value = { ...consultationRequestFilter.value }
  }

  function removeHospitalFilter(hospitalId: string) {
    remove(consultationRequestFilter.value.hospitals, (h) => h.id === hospitalId)
  }

  function removeTargetMedicalFieldFilter(medicalField: string) {
    remove(consultationRequestFilter.value.targetMedicalFields, (f) => f === medicalField)
  }

  function removeDepartmentFilter(departmentName: string) {
    remove(consultationRequestFilter.value.departments, (d) => d === departmentName)
  }

  function removeUrgencyFilter(urgency: UrgencyDTO) {
    remove(consultationRequestFilter.value.urgencies, (u) => u === urgency)
  }

  function removeAssigneeFilter(assigneeUserName: string) {
    remove(consultationRequestFilter.value.assignees, (a) => a.userName === assigneeUserName)
  }

  function removeRequesterFilter(requesterUserName: string) {
    remove(consultationRequestFilter.value.requesters, (r) => r.userName === requesterUserName)
  }

  return {
    incomingDirectConsultations,
    isInitialLoadingIncomingDirectConsultations,
    outgoingDirectConsultations,
    isInitialLoadingOutgoingDirectConsultations,
    incomingScheduledConsultations,
    outgoingScheduledConsultations,
    currentScheduledConsultation,
    isLoadingCurrentConsultation,
    currentScheduledConsultationReports,
    isLoadingCurrentConsultationReports,
    consultationFilter: consultationRequestFilter,
    consultationFilterInput: consultationRequestFilterInput,
    commitConsultationRequestFilterInput,
    refreshUsers,
    areUsersLoading,
    resetConsultationRequestFilterInput,
    removeHospitalFilter,
    removeTargetMedicalFieldFilter,
    removeDepartmentFilter,
    removeUrgencyFilter,
    removeAssigneeFilter,
    removeRequesterFilter,
    areReportsPending,
    isEditingRecipients,
    getConsultationRequestsWithActiveCall: getDirectConsultationsWithActiveCall,
    getScheduledConsultationsWithActiveCall,
    refreshScheduledConsultationById,
    refreshScheduledConsultationReportsById,
    refreshIncomingConsultationRequestList,
    refreshOutgoingConsultationRequestList,
    deleteOutgoingDirectConsultationById,
    deleteOutgoingScheduledConsultationById,
    completeScheduledConsultationById,
    hideScheduledConsultationById,
    updateScheduledConsultationCases,
    updateScheduledConsultationRecipients,
    getIncomingDirectConsultationById,
    getIncomingScheduledConsultationById,
    getOutgoingDirectConsultationById,
    getOutgoingScheduledConsultationById,
    getDirectConsultationById
  }
})
